import { useCallback, useMemo } from 'react';
import { isString, useDebugger, useMap } from '@chkp/mwc-common';

const _parseTree = ({
	root,
	getRecordKey,
	getRecordChildren,
	isExpandedUid,
	includeRoot,
}) => {
	const rowKeys = []; // idx => key
	const nodes = new Map(); // key => record
	const parents = new Map(); // key => parent key

	function parse(node, parentKey, isRoot) {
		const key = getRecordKey(node);
		nodes.set(key, node);

		if (isRoot && !includeRoot) {
			// Ignore root and add all it's children
			const children = getRecordChildren(node);
			children?.forEach((child) => parse(child, key));
		} else {
			rowKeys.push(key);
			parentKey && parents.set(key, parentKey);
			if (isExpandedUid(key)) {
				const children = getRecordChildren(node);
				children && children.forEach((child) => parse(child, key));
			}
		}
	}

	parse(root, null, true);

	return { nodes, rowKeys, parents };
};

/**
 * @param root
 * @param rowKey
 * @param rowChildren
 * @param includeRoot
 * @param defaultExpanded
 * @param debug
 * @return {{getRecordByIndex: function(*): unknown, isExpanded: function(*=): *, rowsCount: number, setExpanded: function(*=, *=): void, getRecordByKey: function(*=): unknown, getParentByKey: function(*=): unknown, clearExpanded: () => void, rowKey: (function(*=))|string, getParentByRecord: function(*=): unknown}}
 */
const useTreeStore = ({
	root,
	rowKey = 'uid',
	rowChildren = 'children',
	includeRoot = true,
	defaultExpanded = true,
	debug,
}) => {
	const getRecordKey = useMemo(
		() => (isString(rowKey) ? (rec) => rec && rec[rowKey] : rowKey),
		[rowKey],
	);

	const getRecordChildren = useMemo(
		() =>
			isString(rowChildren) ? (rec) => rec && rec[rowChildren] : rowChildren,
		[rowChildren],
	);

	const {
		get: isExpandedUid_,
		set: setExpandedUid,
		clear: clearExpanded,
	} = useMap(); // uid => bool (only includes modified nodes)

	const isExpandedUid = useCallback(
		(uid) => isExpandedUid_(uid, defaultExpanded),
		[defaultExpanded, isExpandedUid_],
	);

	const isExpanded = useCallback((rec) => isExpandedUid(getRecordKey(rec)), [
		getRecordKey,
		isExpandedUid,
	]);

	const setExpanded = useCallback(
		(rec, expanded) => setExpandedUid(getRecordKey(rec), expanded),
		[getRecordKey, setExpandedUid],
	);

	const treeApi = useMemo(() => {
		const { nodes, rowKeys, parents } = _parseTree({
			root,
			getRecordKey,
			getRecordChildren,
			isExpandedUid,
			includeRoot,
		});

		const getRecordByIndex = (idx) => nodes.get(rowKeys[idx]);
		const getRecordByKey = (key) => nodes.get(key);
		const getParentByKey = (key) => nodes.get(parents.get(key));
		const getParentByRecord = (rec) =>
			nodes.get(parents.get(getRecordKey(rec)));
		const rowsCount = rowKeys.length;

		return {
			getRecordByIndex,
			getRecordByKey,
			getParentByKey,
			getParentByRecord,
			rowsCount,
		};
	}, [getRecordChildren, getRecordKey, includeRoot, isExpandedUid, root]);

	const api = useMemo(
		() => ({
			...treeApi,
			isExpanded,
			setExpanded,
			clearExpanded,
			rowKey: getRecordKey,
		}),
		[treeApi, isExpanded, setExpanded, clearExpanded, getRecordKey],
	);

	if (debug) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useDebugger('usePagingStore', {
			root,
			rowKey,
			rowChildren,
			includeRoot,
			defaultExpanded,
			getRecordKey,
			getRecordChildren,
			isExpandedUid_,
			setExpandedUid,
			clearExpanded,
			isExpandedUid,
			isExpanded,
			setExpanded,
			treeApi,
			api,
		});
	}

	return api;
};

export { useTreeStore };
